import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { AppIcon } from '../../../components/icons/AppIcon';
import { notify } from '../../../utils/toast';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { fetchOrganizationLimits } from '../../../services/modules/subscription/subscription.limits';
import { updateSubscriptionQuantity, fetchAvailablePlans } from '../../../services/modules/subscription/subscription.api';
import { supabase } from '../../../lib/supabase';
import { formatCurrency } from '../../../utils/formatters';
interface InviteMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onInvite: (email: string, role: 'admin' | 'member' | 'viewer') => Promise<string | undefined>;
    isLoading?: boolean;
}

const ROLE_OPTIONS = [
    { value: 'member', label: 'Membro (Padrão)' },
    { value: 'admin', label: 'Administrador' },
    { value: 'viewer', label: 'Visualizador' },
];

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ isOpen, onClose, onInvite, isLoading }) => {
    const { currentOrganization, refreshOrganizations } = useOrganization();
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
    const [inviteLink, setInviteLink] = useState<string | null>(null);

    // Estados de Limite e Upgrade
    const [currentUsage, setCurrentUsage] = useState({ used: 0, max: 0 });
    const [isCheckingLimits, setIsCheckingLimits] = useState(false);
    const [isUpgradeNeeded, setIsUpgradeNeeded] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState<{
        price: number;
        planName: string;
    } | null>(null);

    // Verificar limites ao abrir
    useEffect(() => {
        if (isOpen && currentOrganization) {
            checkLimits();
        } else {
            setInviteLink(null);
            setEmail('');
            setIsUpgradeNeeded(false);
        }
    }, [isOpen, currentOrganization]);

    const checkLimits = async () => {
        if (!currentOrganization) return;

        try {
            setIsCheckingLimits(true);

            // 1. Buscar limites de uso
            const limits = await fetchOrganizationLimits(currentOrganization.id);

            const { count: pendingInvites } = await supabase
                .from('organization_invites')
                .select('*', { count: 'exact', head: true })
                .eq('organization_id', currentOrganization.id)
                .eq('status', 'pending');

            const totalUsed = limits.users.current + (pendingInvites || 0);
            const maxUsers = limits.users.max;

            setCurrentUsage({ used: totalUsed, max: maxUsers });

            if (totalUsed >= maxUsers) {
                setIsUpgradeNeeded(true);

                // 2. Buscar dados da assinatura apenas se precisar de upgrade
                const plans = await fetchAvailablePlans();
                const currentPlanId = currentOrganization.plan || 'pro';
                const planDetails = plans.find(p => p.id === currentPlanId)
                    ?? plans.find(p => p.name.toLowerCase() === currentPlanId.toLowerCase());
                const price = planDetails?.pricePerUser || 59.90;
                const planName = planDetails?.name || currentPlanId;

                setSubscriptionData({
                    price,
                    planName: planName.charAt(0).toUpperCase() + planName.slice(1),
                });
            } else {
                setIsUpgradeNeeded(false);
            }

        } catch (error) {
            console.error('Erro ao verificar limites/assinatura:', error);
        } finally {
            setIsCheckingLimits(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // Fluxo de Upgrade Automático
            if (isUpgradeNeeded) {
                // Verificar se org tem assinatura Stripe ativa para poder fazer upgrade
                const hasActiveSubscription =
                    currentOrganization?.subscriptionStatus === 'active' ||
                    currentOrganization?.subscriptionStatus === 'past_due';

                if (!hasActiveSubscription) {
                    // Org em trial ou sem assinatura: não pode fazer upgrade automático via Stripe
                    notify.error(
                        'Seu plano de teste atingiu o limite de usuários. Assine um plano pago para convidar mais membros.'
                    );
                    return;
                }

                const priceFormatted = subscriptionData ? formatCurrency(subscriptionData.price) : 'R$ 89,90';

                const confirmed = await notify.confirm(
                    'Limite de Usuários Atingido',
                    {
                        description: (
                            <div className="flex flex-col gap-2 pt-1 text-sm">
                                <p className="text-secondary leading-relaxed">
                                    Seu plano permite <strong className="text-default">{currentUsage.max} usuários</strong> e você já tem <strong className="text-default">{currentUsage.used} ocupados</strong>.
                                </p>
                                <p className="text-secondary leading-relaxed">
                                    Deseja adicionar <strong>+1 licença</strong> por <strong>{priceFormatted}/mês</strong> e enviar o convite agora?
                                </p>
                            </div>
                        ),
                        confirmText: 'Adicionar e Convidar',
                        cancelText: 'Cancelar'
                    }
                );

                if (!confirmed) return;

                setIsUpgrading(true);

                // Atualizar assinatura (+1 licença) no Stripe
                const newQuantity = currentUsage.max + 1;
                if (!currentOrganization?.id) {
                    notify.error('Organização não selecionada.');
                    return;
                }
                await updateSubscriptionQuantity(currentOrganization.id, newQuantity);
                await refreshOrganizations();

                notify.success(`Plano atualizado para ${newQuantity} licenças! Enviando convite...`);
            }

            // Enviar convite
            const link = await onInvite(email, role);
            if (link) {
                setInviteLink(link);
                notify.success('Convite gerado com sucesso!');
                await checkLimits();
                if (!isUpgradeNeeded) await refreshOrganizations();
            } else {
                handleClose();
            }
        } catch (error: any) {
            console.error('Erro no fluxo de convite:', error);
            notify.error(error.message || 'Erro ao processar solicitação');
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleClose = () => {
        setEmail('');
        setInviteLink(null);
        setIsUpgradeNeeded(false);
        onClose();
    };

    const handleCopyLink = () => {
        if (!inviteLink) return;
        navigator.clipboard.writeText(inviteLink);
        notify.success('Link copiado para a área de transferência!');
    };

    if (!isOpen) return null;

    const isProcessing = isLoading || isUpgrading || isCheckingLimits;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md">
            <Modal.Header onClose={handleClose}>
                {inviteLink ? 'Convite Gerado' : 'Convidar Membro'}
            </Modal.Header>

            <Modal.Body>
                {inviteLink ? (
                    <div className="space-y-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                            <div className="bg-green-100 p-2 rounded-full">
                                <AppIcon name="check" className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-green-900">Convite criado com sucesso!</h4>
                                <p className="text-sm text-green-700 mt-1">
                                    Copie o link abaixo e envie para <strong>{email}</strong>. Expira em 7 dias.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-default">Link de convite</label>
                            <div className="flex gap-2">
                                <Input readOnly value={inviteLink} className="bg-surface-subtle font-mono text-sm" />
                                <Button type="button" onClick={handleCopyLink} variant="secondary" title="Copiar link">
                                    <AppIcon name="copy" className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form id="invite-form" onSubmit={handleSubmit} className="space-y-4">
                        {isUpgradeNeeded && !isCheckingLimits && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col gap-2">
                                <div className="flex items-center gap-2 text-amber-800 font-medium">
                                    <AppIcon name="alert-triangle" className="w-4 h-4" />
                                    <span>Limite de Usuários Atingido</span>
                                </div>
                                <p className="text-sm text-amber-700">
                                    Seu plano atual tem <strong>{currentUsage.max}/{currentUsage.max}</strong> vagas ocupadas.
                                </p>
                                <p className="text-sm text-amber-700">
                                    Ao convidar, uma <strong>licença extra</strong> será adicionada automaticamente (+{subscriptionData ? formatCurrency(subscriptionData.price) : 'R$ --'}/mês).
                                </p>
                            </div>
                        )}

                        <Input
                            label="Email do Usuário"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="exemplo@email.com"
                            helperText="Após criar, você receberá um link exclusivo para compartilhar com esta pessoa."
                            required
                            disabled={isProcessing}
                        />
                        <Select
                            label="Função"
                            value={role}
                            onChange={(e) => setRole(e.target.value as any)}
                            options={ROLE_OPTIONS}
                            disabled={isProcessing}
                        />
                    </form>
                )}
            </Modal.Body>

            <Modal.Footer>
                {inviteLink ? (
                    <>
                        <Button onClick={handleCopyLink} variant="secondary">
                            Copiar link
                        </Button>
                        <Button onClick={handleClose} variant="default">
                            Concluído
                        </Button>
                    </>
                ) : (
                    <>
                        <Button type="button" variant="secondary" onClick={handleClose} disabled={isProcessing}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="invite-form"
                            variant="default"
                            disabled={isProcessing}
                            className={isUpgradeNeeded ? "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500" : ""}
                        >
                            {isCheckingLimits ? 'Verificando...' :
                                isUpgrading ? 'Atualizando Plano...' :
                                    isLoading ? 'Gerando...' :
                                        isUpgradeNeeded ? 'Adicionar Vaga e Convidar' : 'Gerar Convite'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default InviteMemberModal;
